/**
 * Redder Supabase-lenker som lander på roten av appen.
 *
 * PROBLEMET
 *
 * Mimin bruker hash-ruter (`https://mimin.no/#/login`). Supabase legger
 * tokenene sine i nettadressens fragment:
 *
 *   https://mimin.no/#access_token=…&refresh_token=…&type=recovery
 *
 * Det er samme sted ruteren leser stien fra. Ruteren ser «access_token=…»,
 * finner ingen rute som heter det, og faller tilbake til innloggingen — mens
 * tokenene forsvinner. For den som klikket på «Tilbakestill passord» i
 * e-posten ser det ut som om lenken bare sender deg til innloggingen igjen.
 *
 * LØSNINGEN
 *
 * Les fragmentet før appen starter, og skriv det om til en rute appen faktisk
 * har. Dette er en redningsluke, ikke hovedveien: nye lenker peker på
 * `auth-callback.html`, som gjør den samme jobben. Men lenker som allerede er
 * sendt ut, og oppsett der Supabase bruker standardmalen sin, lander her.
 */

export type AuthLenkeMaal = { hash: string } | null;

/** Typene Supabase sender folk tilbake med et engangstoken. */
const TOKENTYPER = ["recovery", "invite", "magiclink", "signup"];

/**
 * Hva fragmentet skal skrives om til, eller null når det ikke er noe å redde
 * (vanlig navigasjon, tom adresse, eller en rute som allerede er gyldig).
 *
 * @param hash fragmentet inkludert `#`, typisk `window.location.hash`
 */
export const finnAuthMaal = (hash: string): AuthLenkeMaal => {
  const rå = (hash ?? "").replace(/^#/, "");
  if (!rå) return null;

  // Alt som starter med «/» er allerede en rute — la det være.
  if (rå.startsWith("/")) return null;

  const params = new URLSearchParams(rå);

  // Utløpt eller brukt lenke: Supabase sender error_* i fragmentet. Uten dette
  // ville en utløpt lenke sett nøyaktig ut som feilen over — stille retur til
  // innloggingen, uten å si hvorfor.
  const feil = params.get("error_code") ?? params.get("error");
  if (feil) {
    return { hash: `#/login?authFeil=${encodeURIComponent(feil)}` };
  }

  const accessToken = params.get("access_token");
  const refreshToken = params.get("refresh_token");
  const type = params.get("type") ?? "";

  if (!accessToken || !refreshToken || !TOKENTYPER.includes(type)) return null;

  // recovery og invite skal sette nytt passord; de øvrige typene har allerede
  // en gyldig økt og hører hjemme på forsiden.
  if (type !== "recovery" && type !== "invite") {
    return { hash: "#/" };
  }

  const søk = new URLSearchParams({
    access_token: accessToken,
    refresh_token: refreshToken,
    type,
  });
  return { hash: `#/set-password?${søk.toString()}` };
};

/**
 * Kjøres før appen monteres. Bytter ut fragmentet uten å legge en ny
 * oppføring i historikken — trykker man «tilbake» skal man ikke havne på en
 * adresse med et engangstoken i.
 */
export const reddAuthLenke = (win: Window = window): void => {
  const mål = finnAuthMaal(win.location.hash);
  if (!mål) return;
  win.location.replace(
    `${win.location.pathname}${win.location.search}${mål.hash}`,
  );
};

/**
 * Kobler på redningsluken: én gang nå, og deretter ved hver fragmentendring.
 *
 * Det siste er ikke teoretisk. Limer du lenken inn i en fane som allerede står
 * åpen, bytter nettleseren bare fragmentet — dokumentet lastes ikke på nytt,
 * og et engangsanrop ved oppstart ville aldri sett tokenet.
 */
export const koblePaaAuthLenke = (win: Window = window): void => {
  reddAuthLenke(win);
  win.addEventListener("hashchange", () => reddAuthLenke(win));
};
